// src/pages/presupuestosPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { 
    Table, 
    Button, 
    Tag, 
    Space, 
    DatePicker, 
    message, 
    Tooltip,
    Select,
    Card,
    Row,
    Col,
    Statistic,
    Typography,
    Spin
} from 'antd';
import { 
    ReloadOutlined, 
    ShoppingCartOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    FileTextOutlined,
    PrinterOutlined
} from '@ant-design/icons';
import { budgetQuoteService } from '../services/budgetQuoteService';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { isAdminRole } from '../permissions';
import dayjs from 'dayjs';
import './invoicesPage.css'; // Reuse styling from invoices page

const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';

const { RangePicker } = DatePicker;
const { Option } = Select;

const PresupuestosPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const isAdmin = isAdminRole(user?.role);
    const { cartDetails = [], clearCart, setCartDetails } = useCart();
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0,
    });
    const [filters, setFilters] = useState({
        status: null,
        dateFrom: null,
        dateTo: null,
    });

    // Fetch quotes on mount and when filters change
    useEffect(() => {
        fetchQuotes(pagination.current, pagination.pageSize);
    }, [filters.status, filters.dateFrom, filters.dateTo]);

    const fetchQuotes = useCallback(async (page = 1, pageSize = 20) => {
        setLoading(true);
        try {
            const response = await budgetQuoteService.getBudgetQuotes({
                ...filters,
                page,
                pageSize
            });

            if (response.success) {
                const sorted = (response.data.quotes || []).slice().sort((a, b) => {
                    if (a.created_at && b.created_at) {
                        return new Date(b.created_at) - new Date(a.created_at);
                    }
                    return Number(b.id) - Number(a.id);
                });
                setQuotes(sorted);
                setPagination({
                    current: page,
                    pageSize: pageSize,
                    total: response.data.pagination?.totalCount || 0,
                });
            } else {
                message.error('Error al cargar presupuestos');
            }
        } catch (error) {
            console.error('Error fetching quotes:', error);
            message.error(`Error al cargar presupuestos: ${error.response?.data?.message || error.message}`);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    const handleLoadToCart = useCallback(async (quoteId, quoteNumber) => {
        try {
            // Warn if cart has items
            if (cartDetails.length > 0) {
                const confirmed = window.confirm(
                    '¿Desea reemplazar el contenido actual del carrito con los items de este presupuesto?'
                );
                if (!confirmed) return;
            }

            message.loading({ content: 'Cargando presupuesto al carrito...', key: 'loadCart' });
            
            const response = await budgetQuoteService.loadQuoteToCart(quoteId);
            
            if (response.success) {
                const { cartItems, quoteMetadata, warnings } = response.data;
                
                // Load items to cart using setCartDetails
                if (setCartDetails && cartItems && cartItems.length > 0) {
                    // Normalize backend items to CartProvider format:
                    // - isCustomProduct (backend flag) → isCustom (CartProvider flag)
                    // - custom items need a negative productId so CartItemList can track them
                    const normalizedItems = cartItems.map((item, index) => {
                        if (item.isCustomProduct || item.is_custom) {
                            return {
                                ...item,
                                isCustom: true,
                                productId: -(Date.now() + index),
                                ProductId: null,
                            };
                        }
                        return {
                            ...item,
                            productId: item.productId || item.ProductId,
                        };
                    });
                    setCartDetails(normalizedItems);
                    
                    // Show warnings if any
                    if (warnings && (warnings.priceChanges > 0 || warnings.deletedProducts > 0)) {
                        message.warning({
                            content: (
                                <div>
                                    <div><strong>Presupuesto {quoteNumber} cargado al carrito</strong></div>
                                    {warnings.priceChanges > 0 && <div>⚠️ {warnings.priceChanges} producto(s) con precios actualizados</div>}
                                    {warnings.deletedProducts > 0 && <div>⚠️ {warnings.deletedProducts} producto(s) ya no disponibles</div>}
                                    <div style={{ marginTop: 8 }}>Por favor revise los precios antes de generar la factura.</div>
                                </div>
                            ),
                            key: 'loadCart',
                            duration: 7
                        });
                    } else {
                        message.success({
                            content: `✅ Presupuesto ${quoteNumber} cargado al carrito (${cartItems.length} items)`,
                            key: 'loadCart',
                            duration: 3
                        });
                    }
                    
                    // Navigate to search page where user can see the cart
                    setTimeout(() => navigate('/Buscador'), 500);
                } else {
                    message.error({ content: 'No se pudieron cargar los items al carrito', key: 'loadCart' });
                }
            } else {
                message.error({ content: 'Error al cargar presupuesto al carrito', key: 'loadCart' });
            }
        } catch (error) {
            console.error('Error loading quote to cart:', error);
            if (error.response?.status === 400 && error.response?.data?.message?.includes('expired')) {
                message.error({ 
                    content: `Este presupuesto ha vencido. Fecha de vencimiento: ${error.response.data.expirationDate || 'N/A'}`,
                    key: 'loadCart',
                    duration: 5
                });
            } else {
                message.error({ 
                    content: `Error al cargar presupuesto: ${error.response?.data?.message || error.message}`,
                    key: 'loadCart'
                });
            }
        }
    }, [cartDetails, setCartDetails, navigate]);

    const handleAcceptQuote = useCallback(async (quoteId, quoteNumber) => {
        try {
            // First, accept the quote
            const response = await budgetQuoteService.updateQuoteStatus(quoteId, 'ACEPTADO');
            if (response.success) {
                message.success(`Presupuesto ${quoteNumber} aceptado`);
                
                // Auto-load to cart
                const confirmed = window.confirm(
                    `Presupuesto ${quoteNumber} aceptado. ¿Desea cargarlo al carrito ahora?`
                );
                
                if (confirmed) {
                    // Load to cart (reuse existing logic)
                    await handleLoadToCart(quoteId, quoteNumber);
                } else {
                    // Just refresh the list
                    fetchQuotes(pagination.current, pagination.pageSize);
                }
            } else {
                message.error('Error al aceptar presupuesto');
            }
        } catch (error) {
            console.error('Error accepting quote:', error);
            message.error(`Error al aceptar presupuesto: ${error.response?.data?.message || error.message}`);
        }
    }, [pagination, fetchQuotes, handleLoadToCart]);

    const handleRejectQuote = useCallback(async (quoteId, quoteNumber) => {
        const reason = window.prompt('Motivo del rechazo (opcional):');
        
        try {
            const response = await budgetQuoteService.updateQuoteStatus(quoteId, 'RECHAZADO', {
                rejectionReason: reason || null
            });
            
            if (response.success) {
                message.success(`Presupuesto ${quoteNumber} rechazado`);
                fetchQuotes(pagination.current, pagination.pageSize);
            } else {
                message.error('Error al rechazar presupuesto');
            }
        } catch (error) {
            console.error('Error rejecting quote:', error);
            message.error(`Error al rechazar presupuesto: ${error.response?.data?.message || error.message}`);
        }
    }, [pagination, fetchQuotes]);

    const handlePrintQuote = useCallback((quoteId, quoteNumber) => {
        // Open print page in new window/tab
        const printPageUrl = `${window.location.origin}/#/print-quote?budgetQuoteId=${quoteId}`;
        window.open(printPageUrl, '_blank');
        message.info(`Abriendo vista de impresión para ${quoteNumber}`);
    }, []);

    const getStatusColor = (status) => {
        const colors = {
            'PENDIENTE': 'blue',
            'ACEPTADO': 'green',
            'RECHAZADO': 'red',
            'VENCIDO': 'orange',
            'CONVERTIDO': 'purple'
        };
        return colors[status] || 'default';
    };

    const formatCurrency = (amount) => {
        return `$ ${Number(amount).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const columns = [
        {
            title: 'Nº Presupuesto',
            dataIndex: 'quote_number',
            key: 'quote_number',
            width: 150,
            fixed: 'left',
        },
        {
            title: 'Cliente',
            dataIndex: 'cliente_razon_social',
            key: 'cliente_razon_social',
            width: 200,
            ellipsis: true,
            render: (text, record) => {
                const clientDisplay = record.cliente_nombre_informal || text;
                return (
                    <div>
                        <div>{clientDisplay || 'Sin cliente'}</div>
                        {record.cliente_nombre_informal && text && record.cliente_nombre_informal !== text && (
                            <div style={{ fontSize: '12px', color: '#888' }}>{text}</div>
                        )}
                        {record.cliente_documento && (
                            <div style={{ fontSize: '12px', color: '#888' }}>
                                CUIT: {record.cliente_documento}
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            title: 'Fecha Emisión',
            dataIndex: 'fecha_emision',
            key: 'fecha_emision',
            width: 160,
            ellipsis: true,
            render: (date, record) => {
                const dateFormatted = dayjs(date.substring(0, 10)).format('DD/MM/YYYY');
                // Get time from created_at if available
                if (record.created_at) {
                    const createdDate = new Date(record.created_at);
                    const hours = String(createdDate.getHours()).padStart(2, '0');
                    const minutes = String(createdDate.getMinutes()).padStart(2, '0');
                    return `${dateFormatted} ${hours}:${minutes}`;
                }
                return dateFormatted;
            },
        },
        {
            title: 'Vencimiento',
            dataIndex: 'fecha_vencimiento',
            key: 'fecha_vencimiento',
            width: 130,
            render: (date, record) => {
                const isExpired = record.is_expired === 1;
                return (
                    <span style={{ color: isExpired ? 'red' : 'inherit' }}>
                        {dayjs(date.substring(0, 10)).format('DD/MM/YYYY')}
                        {isExpired && <div style={{ fontSize: '11px' }}>(Vencido)</div>}
                    </span>
                );
            },
        },
        {
            title: 'Items',
            dataIndex: 'item_count',
            key: 'item_count',
            width: 80,
            align: 'center',
        },
        {
            title: 'Total',
            dataIndex: 'total',
            key: 'total',
            width: 130,
            align: 'right',
            render: (total) => <strong>{formatCurrency(total)}</strong>,
        },
        {
            title: 'Estado',
            dataIndex: 'status',
            key: 'status',
            width: 120,
            render: (status) => <Tag color={getStatusColor(status)}>{status}</Tag>,
        },
        {
            title: 'Acciones',
            key: 'actions',
            width: 220,
            fixed: 'right',
            render: (_, record) => (
                <Space size="small" wrap>
                            {/* Always available actions */}
                    <Tooltip title="Imprimir / Ver">
                        <Button
                            type="default"
                            size="small"
                            icon={<PrinterOutlined />}
                            onClick={() => {
                                if (isDemoMode) { message.info('Vista previa del Presupuesto en formato PDF'); return; }
                                handlePrintQuote(record.id, record.quote_number);
                            }}
                        />
                    </Tooltip>
                    
                    {/* Status-specific actions */}
                    {(record.status === 'PENDIENTE' || record.status === 'ACEPTADO') && (
                        <Tooltip title="Cargar al Carrito">
                            <Button
                                type="primary"
                                size="small"
                                icon={<ShoppingCartOutlined />}
                                onClick={() => {
                                    if (isDemoMode) { message.info('Items del presupuesto disponibles para convertir en factura'); return; }
                                    handleLoadToCart(record.id, record.quote_number);
                                }}
                            />
                        </Tooltip>
                    )}
                    
                    {record.status === 'PENDIENTE' && (
                        <>
                            <Tooltip title="Aceptar">
                                <Button
                                    type="default"
                                    size="small"
                                    icon={<CheckCircleOutlined />}
                                    onClick={() => {
                                        if (isDemoMode) { message.success('Presupuesto marcado como Aceptado por el cliente'); return; }
                                        handleAcceptQuote(record.id, record.quote_number);
                                    }}
                                    style={{ color: '#52c41a', borderColor: '#52c41a' }}
                                />
                            </Tooltip>
                            
                            <Tooltip title="Rechazar">
                                <Button
                                    type="default"
                                    size="small"
                                    danger
                                    icon={<CloseCircleOutlined />}
                                    onClick={() => {
                                        if (isDemoMode) { message.warning('Presupuesto marcado como Rechazado'); return; }
                                        handleRejectQuote(record.id, record.quote_number);
                                    }}
                                />
                            </Tooltip>
                        </>
                    )}
                    
                    {record.status === 'CONVERTIDO' && record.converted_to_invoice_id && (
                        <Tooltip title="Ver factura relacionada">
                            <Button
                                type="default"
                                size="small"
                                icon={<FileTextOutlined />}
                                onClick={() => {
                                    if (isDemoMode) { message.info('Factura generada a partir de este presupuesto — ver en Facturas'); return; }
                                    navigate(`/Invoices`);
                                }}
                                style={{ color: '#722ed1', borderColor: '#722ed1' }}
                            />
                        </Tooltip>
                    )}
                </Space>
            ),
        },
    ];

    // Calculate statistics
    const stats = {
        total: quotes.length,
        pendientes: quotes.filter(q => q.status === 'PENDIENTE').length,
        aceptados: quotes.filter(q => q.status === 'ACEPTADO').length,
        rechazados: quotes.filter(q => q.status === 'RECHAZADO').length,
        convertidos: quotes.filter(q => q.status === 'CONVERTIDO').length,
        totalAmount: quotes.reduce((sum, q) => sum + (parseFloat(q.total) || 0), 0)
    };

    return (
        <div className="invoices-page-container">
            <div className="invoices-header">
                <h1>📋 Presupuestos</h1>
                <Button
                    type="primary"
                    icon={<ReloadOutlined />}
                    onClick={() => fetchQuotes(pagination.current, pagination.pageSize)}
                    loading={loading}
                >
                    Actualizar
                </Button>
            </div>

            {/* Statistics Cards — admin only */}
            {isAdmin && <Row gutter={16} style={{ marginBottom: 24, display: 'flex' }}>
                <Col xs={24} sm={12} md={6} style={{ display: 'flex' }}>
                    <Card size="small" style={{ borderLeft: '4px solid #1890ff', width: '100%' }}>
                        {loading ? (
                            <div style={{ textAlign: 'center', padding: '40px 0' }}><Spin size="large" /></div>
                        ) : (
                            <Statistic 
                                title={<Typography.Text style={{ fontSize: '16px', fontWeight: 600 }}>TOTAL PRESUPUESTOS</Typography.Text>}
                                value={stats.total}
                                valueStyle={{ color: '#1890ff', fontSize: '24px' }}
                            />
                        )}
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6} style={{ display: 'flex' }}>
                    <Card size="small" style={{ borderLeft: '4px solid #faad14', width: '100%' }}>
                        {loading ? (
                            <div style={{ textAlign: 'center', padding: '40px 0' }}><Spin size="large" /></div>
                        ) : (
                            <Statistic 
                                title={<Typography.Text style={{ fontSize: '16px', fontWeight: 600 }}>PENDIENTES</Typography.Text>}
                                value={stats.pendientes}
                                valueStyle={{ color: '#faad14', fontSize: '24px' }}
                            />
                        )}
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6} style={{ display: 'flex' }}>
                    <Card size="small" style={{ borderLeft: '4px solid #52c41a', width: '100%' }}>
                        {loading ? (
                            <div style={{ textAlign: 'center', padding: '40px 0' }}><Spin size="large" /></div>
                        ) : (
                            <Statistic 
                                title={<Typography.Text style={{ fontSize: '16px', fontWeight: 600 }}>ACEPTADOS</Typography.Text>}
                                value={stats.aceptados}
                                valueStyle={{ color: '#52c41a', fontSize: '24px' }}
                            />
                        )}
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6} style={{ display: 'flex' }}>
                    <Card size="small" style={{ borderLeft: '4px solid #ff4d4f', width: '100%' }}>
                        {loading ? (
                            <div style={{ textAlign: 'center', padding: '40px 0' }}><Spin size="large" /></div>
                        ) : (
                            <Statistic 
                                title={<Typography.Text style={{ fontSize: '16px', fontWeight: 600 }}>RECHAZADOS</Typography.Text>}
                                value={stats.rechazados}
                                valueStyle={{ color: '#ff4d4f', fontSize: '24px' }}
                            />
                        )}
                    </Card>
                </Col>
            </Row>}

            {/* Filters */}
            <div className="invoices-filters" style={{ marginBottom: 16 }}>
                <Space wrap>
                    <Select
                        style={{ width: 200 }}
                        placeholder="Filtrar por estado"
                        allowClear
                        value={filters.status}
                        onChange={(value) => setFilters({ ...filters, status: value })}
                    >
                        <Option value="PENDIENTE">Pendiente</Option>
                        <Option value="ACEPTADO">Aceptado</Option>
                        <Option value="RECHAZADO">Rechazado</Option>
                        <Option value="VENCIDO">Vencido</Option>
                        <Option value="CONVERTIDO">Convertido</Option>
                    </Select>
                    
                    <RangePicker
                        format="DD/MM/YYYY"
                        placeholder={['Desde', 'Hasta']}
                        onChange={(dates) => {
                            setFilters({
                                ...filters,
                                dateFrom: dates ? dates[0].format('YYYY-MM-DD') : null,
                                dateTo: dates ? dates[1].format('YYYY-MM-DD') : null,
                            });
                        }}
                    />
                    
                    <Button
                        onClick={() => setFilters({ status: null, dateFrom: null, dateTo: null })}
                    >
                        Limpiar Filtros
                    </Button>
                </Space>
            </div>

            {/* Table */}
            <Table
                columns={columns}
                dataSource={quotes}
                loading={loading}
                rowKey="id"
                pagination={{
                    ...pagination,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} presupuestos`,
                    onChange: (page, pageSize) => {
                        fetchQuotes(page, pageSize);
                    },
                }}
                scroll={{ x: 1200 }}
            />
        </div>
    );
};

export default PresupuestosPage;
